import { useCallback, useEffect, useRef, useState } from "react";
import { copyText } from "../lib/clipboard";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface CopyFieldProps {
	/** Реф-ссылка, код приглашения, идентификатор — всё моноширинное. */
	value: string;
	/** Что показать вместо `value`, если строка длиннее поля. */
	display?: string;
	label?: string;
	/** Доступное имя кнопки копирования. Обязательно — умолчаний у кита нет. */
	copyLabel: string;
	/** Имя той же кнопки после копирования. Обязательно. */
	copiedLabel: string;
	onCopy?: (value: string) => void;
	/**
	 * Буфер отказал — показать это человеку. Обязателен, и это не формальность:
	 * молчащий отказ выглядит как успешное копирование, и человек вставляет
	 * прошлое содержимое буфера (в админке — вместо секрета, который показан
	 * один раз). Текст сообщения кит не знает: подписей у него нет вовсе.
	 */
	onCopyError: () => void;
	className?: string;
}

/**
 * Моноширинная строка на серой плашке r10 с кнопкой копирования.
 *
 * Подтверждение живёт ровно 2 секунды и снимается через ref-таймер: без
 * очистки при размонтировании React ругается на setState у мёртвого компонента,
 * а поле копирования часто стоит внутри поповера, который закрывают сразу
 * после нажатия.
 */
export function CopyField({
	value,
	display,
	label,
	copyLabel,
	copiedLabel,
	onCopy,
	onCopyError,
	className,
}: CopyFieldProps) {
	const [copied, setCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (timer.current) clearTimeout(timer.current);
		},
		[],
	);

	// `void`, а не async-обработчик на `onClick`: промис, отданный React'у,
	// никто не ждёт, и его отказ уезжал в Hawk необработанным rejection'ом
	// («Document is not focused», бой 24.08.2026) — вместо сообщения человеку.
	const copy = useCallback(() => {
		void copyText(value).then((ok) => {
			if (!ok) {
				onCopyError();
				return;
			}
			onCopy?.(value);
			setCopied(true);
			if (timer.current) clearTimeout(timer.current);
			timer.current = setTimeout(() => setCopied(false), 2000);
		});
	}, [value, onCopy, onCopyError]);

	// Кнопка — вся плашка, а не только глиф справа. Человек метится в ссылку,
	// которую видит, и промах по иконке 20px читался как «не копируется».
	// Вложенной кнопки внутри нет намеренно: две кнопки друг в друге — невалидная
	// разметка, и скринридер объявлял бы их обеими.
	return (
		<button
			type="button"
			onClick={copy}
			aria-label={copied ? copiedLabel : copyLabel}
			className={cn(
				"flex w-full cursor-pointer items-center gap-2.5 rounded-control bg-sunken px-3.5 py-3 text-left",
				"transition-colors duration-150 ease-smooth hover:bg-btn-secondary-hover",
				"focus-ring",
				className,
			)}
		>
			{label ? <span className="text-caps-sm text-fg-muted">{label}</span> : null}
			<span className="t-num-xs min-w-0 flex-grow truncate">{display ?? value}</span>
			<span
				aria-hidden="true"
				className={cn(
					"inline-flex shrink-0 items-center text-fg-muted",
					"transition-colors duration-150 ease-smooth",
					copied && "text-brand-text",
				)}
			>
				<Icon name={copied ? "Check" : "Copy"} size={20} />
			</span>
		</button>
	);
}
